import { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";

import FormInputs from "@/components/Organisms/HookFormInputs/V1";
import Typography from "@/foundation/Typography/V2";

import useBookSlotForm from "./useBookSlotForm";

import { classNameGenerator } from "@/utils";

import { useAppSelector } from "@/hooks";

import styles from "./BookSlotForm.module.scss";

import yogaGoals from "@/redux/BootCampReducer/BootcampData/yogaGoals";
import nutritionGoals from "@/redux/BootCampReducer/BootcampData/nutritionGoals";

interface BookSlotFormProps {
  showModal: any;
  setFormData: any;
  formData: any;
}

const BookSlotForm: React.FC<BookSlotFormProps> = ({
  showModal,
  setFormData,
  formData
}) => {
  const { packageData } = showModal;

  const cls = classNameGenerator(styles);

  const { data } = useAppSelector((state) => state.bootCamp);

  const isGeneralYogaPage = data?.pageType === "GENERAL_YOGA";
  const isGeneralNutritionPage = data?.pageType === "GENERAL_NUTRITION";

  const isYogaPage = [
    "GENERAL_YOGA",
    "YOGA_BOOTCAMP",
    "YOGA_FOR_HEALTH_CONDITION",
  ]?.includes(data?.pageType);

  const [packData, setPackData] = useState(packageData || {});
  const [formError, setFormError] = useState("");

  const { isTrialPack, showCoupon = true, footerText = "", totalHourstext = "", promoCode = "", shouldPreApplyCoupon = false, showPromoCode=false } = packData || {};
  const isAnatomyPage = data?.conditionKey === "yoga-trainer-foundation-program"
  const {
    methods,
    isLoading,
    onBlur,
    packPurchaseHandler,
    isPromoCodeLoading,
    isPromoCodeApplied,
    promoCodeData,
    promoCodeError,
    handlePromoCodeChange,
    applyPromoCode,
    removeAppliedPromoCode,
    promoCodeButtonText,
    preApplyPromoCode
  } = useBookSlotForm({ packData, setFormError, setFormData });

  const { reset } = methods

  useEffect(() => {
    setPackData(packageData);
  }, [packageData]);

  useEffect(() => {
    if (promoCode && shouldPreApplyCoupon) {
      preApplyPromoCode(promoCode)
    }
  }, [promoCode])

  useEffect(() => {
    if (showModal) {
      reset((prev) => ({
        ...prev,
        ...formData
      }))
      setPackData((prevState) => ({
        ...prevState,
        originalPrice: packageData?.price,
      }));
    } else {
      reset()
      setPackData(null);
      removeAppliedPromoCode();
    }
  }, [showModal, removeAppliedPromoCode]);

  useEffect(() => {
    if (promoCodeData) {
      setPackData((prevState) => {
        const discountedPrice =
          Number(promoCodeData?.original_price) -
          Number(promoCodeData?.new_price);
        return {
          ...prevState,
          price: promoCodeData?.new_price,
          originalPrice: promoCodeData?.original_price,
          discountedPrice,
        };
      });
    } else {
      setPackData((prevState) => {
        return {
          ...prevState,
          price: prevState?.originalPrice,
          discountedPrice: null,
        };
      });
    }
  }, [promoCodeData]);
  const components = [
    {
      name: "name",
      props: {
        name: "name",
        className: cls("name"),
        removeAppliedPromoCode
      },
    },
    ...(!isAnatomyPage
      ? [{
        name: "email",
        props: {
          name: "email",
          className: cls("email"),
          onBlur,
          removeAppliedPromoCode
        },
      }
      ] : [{
        name: "phone",
        props: {
          name: "mobile",
          className: cls("phone"),
          onBlur,
          removeAppliedPromoCode
        },
      }]),
    ...(isAnatomyPage
      ? [{
        name: "email",
        props: {
          name: "email",
          className: cls("email"),
          onBlur,
          removeAppliedPromoCode
        },
      }
      ] : [{
        name: "phone",
        props: {
          name: "mobile",
          className: cls("phone"),
          onBlur,
          removeAppliedPromoCode
        },
      }]),
    ...(isGeneralYogaPage || isGeneralNutritionPage
      ? [
        {
          name: "select",
          props: {
            name: "nutritionGoalID",
            className: cls("nutritionGoal"),
            onBlur,
            placeholder: "Focus area",
            options: isGeneralYogaPage ? yogaGoals : nutritionGoals,
            removeAppliedPromoCode
          },
        },
      ]
      : []),
    ...(totalHourstext ?
      [{
        name: "numberInput",
        props: {
          name: "totalHours",
          placeholder: "0",
          heading: totalHourstext
        },
      }] : []),
    ...(packageData?.price > 0 && (!isTrialPack || showPromoCode) && showCoupon
      ? [
        {
          name: "promoCode",
          props: {
            name: "promoCode",
            className: cls("promoCode"),
            onBlur,
            placeholder: "Enter promo code",
            onChange: handlePromoCodeChange,
            isLoading: isPromoCodeLoading,
            ctaText: promoCodeButtonText,
            isPromoCodeApplied,
            onCTAClick: isPromoCodeApplied
              ? removeAppliedPromoCode
              : applyPromoCode,
            defaultValue: shouldPreApplyCoupon ? promoCode : ""
          },
        }, removeAppliedPromoCode
      ]
      : []),
    ...(data?.allPackages?.showOnBookingForm && !isTrialPack ? [
      {
        name: "packages",
        props: {
          name: "packages",
          className: cls("packages"),
          onBlur,
          removePromoCode: isPromoCodeApplied
            ? removeAppliedPromoCode
            : () => { }
        }
      }
    ]
      : []

    ),
    {
      name: "submit",
      props: {
        children: (
          <Typography
            variant="span"
            dangerouslySetInnerHTML={{
              __html:
                packData?.price > 0
                  ? isPromoCodeApplied
                    ? `Confirm & Pay ${packData?.currencySymbol}&nbsp;${packData?.price} <span class="strikeout">${packData?.currencySymbol}&nbsp;${packData?.originalPrice}</span>`
                    : `Confirm & Pay ${packData?.currencySymbol}&nbsp;${packData?.price}`
                  : "Confirm",
            }}
          />
        ),
        className: cls("submitButton"),
        isLoading,
      },
    },
    {
      props: {
        children: (
          <Typography
            variant="paragraph1"
            className={cls("infoText")}
            dangerouslySetInnerHTML={{
              __html: footerText ? footerText
                : !showCoupon ? ""
                  : isTrialPack
                    ? isYogaPage
                      ? "Once enrolled, download the Shyft app to <br/>schedule your trial class"
                      : "Once enrolled, download the Shyft app to attend or <br/>reschedule your pre-consultation"
                    : isYogaPage
                      ? "Once enrolled, download the Shyft app to attend or <br/>reschedule your yoga classes"
                      : "Once enrolled, download the Shyft app to <br/>schedule your appointments",
            }}
          />
        ),
      },
    },
    {
      name: "error",
      props: {
        error: formError,
        className: cls("formError"),
      },
    },
  ];

  return (
    <FormProvider {...methods}>
      <form className={cls("formWrapper")} onSubmit={packPurchaseHandler}>
        <FormInputs
          setFormData={setFormData}
          formData={formData}
          components={components}
        />
      </form>
    </FormProvider>
  );
};

export default BookSlotForm;
